import React, { useState, useEffect, Fragment, useCallback, useRef } from 'react';
import { useNavigate} from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Dialog, Transition } from '@headlessui/react';
import Flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import { PlusSmallIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import Select from 'react-select';
import Header from '../common/Header';
import Footer from '../common/Footer';
import Inputmask from 'inputmask';
import { usDate, getData, notify, postData, localData } from '../../functions';
import config from '../../config';
import { PulseLoader } from 'react-spinners';

const GenderOptions = [
    { value: 'M', label: 'Male' },
    { value: 'F', label: 'Female' },
    { value: 'O', label: 'Others' },
];

const ChartStatus = [
    { value: 0, label: 'All' },
    { value: 1, label: 'Incomplete' },
    { value: 2, label: 'Done' },
];

function WaitingRoom() {
    const [defaultFetched, setdefaultFetched] = useState(false);
    const [waitingRoomDate, setWaitingRoomDate] = useState(localData('waiting_room_date') || '');
    const [waitingRoomPatient, setWaitingRoomPatient] = useState([]);
    const [addPatientOpen, setAddPatientOpen] = useState(false);
    const [loadDefaultData, setLoadDefaultData] = useState(false);
    const [waitingRoomStatus, setDaitingRoomStatus] = useState(null);
    const [waitingRoomStatusID, setDaitingRoomStatusID] = useState(localData('waiting_room_status'));
    const [insurance, setInsurance] = useState([]);
    const [vtOptions, setVtOptions] = useState([]);
    const [practiceOptions, setPractioceOptions] = useState([]);
    const [selectedPractice, setSelectedPractice] = useState(null);
    const [practiceID, setPracticeID] = useState(localData('practice_id'));
    const goto = useNavigate();
    const userInfo = localData('user');
    const searchInputValue = localData('search');
    const inputRef = useRef(null);


    


    const handelWaitingRoomDos = (event) => {
        const date = event.target.value;
        const validDate = isValidDate(date);
        if (validDate) {
            setLoadDefaultData(false);
    
            setWaitingRoomDate(date)
            localStorage.setItem('waiting_room_date', date);
            const searchValue = document.querySelector('input[name="search"]').value;
            
            fetchWaitingRoomData(practiceID, date, waitingRoomStatusID, searchValue)
        }
    }

    const isValidDate = (date) => {
        const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
        return dateRegex.test(date);
    };

    const handelSelectPractice = (selectedOption) => {
        setLoadDefaultData(false);

        const newPracticeID = selectedOption.value;
        setPracticeID(newPracticeID);
        setSelectedPractice(selectedOption);
        localStorage.setItem('practice_id', newPracticeID);
        const searchValue = document.querySelector('input[name="search"]').value;
        fetchWaitingRoomData(newPracticeID, waitingRoomDate, waitingRoomStatusID, searchValue)
    }

    const handelSelectStatus = (selectedOption) => {
        setLoadDefaultData(false);

        const newStatusID = selectedOption.value;
        setDaitingRoomStatusID(newStatusID);
        setDaitingRoomStatus(selectedOption);
        localStorage.setItem('waiting_room_status', newStatusID);
        const searchValue = document.querySelector('input[name="search"]').value;
        fetchWaitingRoomData(practiceID, waitingRoomDate, newStatusID, searchValue)
    }

    
    const fetchWaitingRoomData = useCallback(async (practice_id, date, statusID, searchValue) => {
        try {
            if (typeof practice_id != "number") { 
                practice_id = null;
            }
            const result = await postData(`${config.chartServerUrl}/search/waiting-room`, true, { practice_id: practice_id, user_id: userInfo?.user_id, dos: date, status: statusID, search:searchValue });
            
            if (result) {
                setWaitingRoomPatient(result);
                setLoadDefaultData(true);
            }
        } catch (error) {
            notify('error', 'Something went wrong');
        }
    }, [userInfo]);

    function clearFilter() {
        setWaitingRoomDate('');
        var date = adjustDate(new Date());
        localStorage.setItem('waiting_room_date', date);
        const searchValue = document.querySelector('input[name="search"]').value;
        fetchWaitingRoomData(practiceID, date, waitingRoomStatusID, searchValue);
        var dosInputs = document.querySelectorAll('input[name="dos"]');
        dosInputs.forEach(function(input) {
            input.value = '';
        });
    }

    function adjustDate(date) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        return `${month}/${day}/${year}`;
    }

    const handlePatientSelect = async (chart) => {

        localStorage.setItem('chart_status', chart.status);
        localStorage.setItem('chart_id', chart.chart_id);
        localStorage.setItem('patient_id', chart.patient_id);
        localStorage.setItem('reason_for_visit_data', chart.reason_for_visit_data);
        localStorage.setItem('medication_data', chart.medication_data);
        localStorage.setItem('assessment_data', chart.assessment_data);
        localStorage.setItem('patient_data', chart.patient_data);
        localStorage.setItem('raw_hpi', chart.raw_hpi);
        localStorage.setItem('custom_hpi', chart.custom_hpi);
        localStorage.setItem('gpt_assessment', null);

        if (chart.status === 1) {    
            goto('/waiting-room/reason');
        } else if (chart.status === 2) {
            goto('/waiting-room/reason/summary' );
        }
    }

    const getInsurances = async () => {
        const result = await getData(`${config.patientServerUrl}/all/insurance`, true);
        if (result) {
            const data = result.map((items) => ({
                value: items.insurance_id,
                label: items.insurance_name,
            }));
            setInsurance(data);
        }
    };

    const getVisitType = async () => {
        const result = await getData(`${config.apiServerUrl}/all/visit_type`, true);
        if (result) { 
            localStorage.setItem('visit_type', JSON.stringify(result));
            const data = result.map((items) => ({
                value: items.vt_id,
                label: items.vt_name,
            }));
            setVtOptions(data);
        }
    };

    const handleAddPatient = async (event) => {
        event.preventDefault();
    
        const formData = new FormData(event.target);
        const formValues = Object.fromEntries(formData.entries());
    
        try {
            const result = await postData(`${config.chartServerUrl}/store/chart`, true, formValues);
        
            if(result.status === 'success'){
                notify('success', result.message);
                if(result?.chart_data){
                    localStorage.setItem('chart_status', result.chart_data.status);
                    localStorage.setItem('chart_id', result.chart_data.chart_id);
                    localStorage.setItem('patient_id', result.chart_data.patient_id);
                    localStorage.setItem('reason_for_visit_data', result.chart_data.reason_for_visit_data);
                    localStorage.setItem('medication_data', result.chart_data.medication_data);
                    localStorage.setItem('assessment_data', result.chart_data.assessment_data);
                    localStorage.setItem('patient_data', result.chart_data.patient_data);
                    goto('/waiting-room/reason');
                }
            } else {
                notify('error', result.message);
            }
            setAddPatientOpen(false);
        } catch (error) {
            notify('error', 'Something went wrong');
        }
    }
    
    const handelSearchPatient = (event) => {
        const searchValue = event.target.value;
        if (searchValue === '') {
            setLoadDefaultData(false);
            localStorage.setItem('search', null);
            fetchWaitingRoomData(practiceID, waitingRoomDate, waitingRoomStatusID, searchValue);
        } else if (event.key === 'Enter' && searchValue.length > 2) {
            setLoadDefaultData(false);
            localStorage.setItem('search', JSON.stringify(searchValue));
            fetchWaitingRoomData(practiceID, waitingRoomDate, waitingRoomStatusID, searchValue);
        }
    }

    const [firstLoad, setFirstLoad] = useState(false);


    const getReasonForVisit = async () => {
    const result = await getData(`${config.apiServerUrl}/all/reason_for_visit_name`, true);
        if (result) { 
            localStorage.setItem('reason_for_visit_options_response', JSON.stringify(result));
        }
    };


    const getFirstLoad = useCallback(async () => {
        if (firstLoad && !defaultFetched) {                     
            setdefaultFetched(true); 
            if(userInfo.practice_info){
                const data = userInfo.practice_info.map((items) => ({
                    value: items.practice_id,
                    label: items.practice_name,
                }));
                setPractioceOptions(data);
            }
            const searchValue = document.querySelector('input[name="search"]').value;
            
            getInsurances();
            getVisitType();
            fetchWaitingRoomData(practiceID, waitingRoomDate, waitingRoomStatusID, searchValue);
            getReasonForVisit();
    
        }


        setFirstLoad(true); 
    }, [firstLoad, fetchWaitingRoomData, defaultFetched,practiceID, waitingRoomDate, waitingRoomStatusID, userInfo]);

    useEffect(() => {
        getFirstLoad();

        if (!userInfo) {
            goto('/login');
        } else {
            localStorage.setItem('chart_status', null);
            
            setTimeout(() => {
                setSelectedPractice(practiceOptions.find(option => option.value === practiceID));
                setDaitingRoomStatus(ChartStatus.find(option => option.value === waitingRoomStatusID));

                const date = document.querySelectorAll('.flatpickr-date');
                date.forEach((input) => {
                    Flatpickr(input, {
                        dateFormat: 'm/d/Y',
                        allowInput: true,
                    });
                });

                Inputmask({
                    mask: '99/99/9999',
                    showMaskOnHover: false,
                    showMaskOnFocus: true,
                }).mask(date);

                const phoneInputs = document.querySelectorAll('.us_phone');
                Inputmask({
                    mask: '(999) 999-9999',
                    showMaskOnHover: false,
                    showMaskOnFocus: true,
                }).mask(phoneInputs);
                
            }, 100);

            
        }
        
      
    }, [goto, getFirstLoad, userInfo, practiceID, practiceOptions, waitingRoomStatusID]);


    return (
        <>
            <Helmet>
                <title>Waiting Room | { config.appName}</title>
                <meta name="description" content="Contact Page Description" />
            </Helmet>

            <Header />

            <div className='container mx-auto mt-7 pb-28 px-2'>
                <div className="relative isolate" style={{ 'minHeight': '71vh' }}>

                    <div className='xl:flex items-center justify-between'>
                        <div className='md:flex items-center justify-between'>
                            <div className='sm:flex lg:w-2/4 md:w-2/3'>
                                <div className='flex items-center'>
                                    <p className="block text-sm font-medium leading-8 w-16 text-gray-800 dark:text-gray-200"> DOS: <span className='text-red-500'>*</span> </p>
                                    <div className="relative w-full">
                                        <input required
                                            type="search"
                                            name="dos"
                                            defaultValue={usDate(waitingRoomDate)}
                                            // value={usDate(waitingRoomDate)}
                                            onInput={handelWaitingRoomDos}
                                            placeholder="MM/DD/YYYY"
                                            autoComplete='off'
                                            className="flatpickr-date dos_picker bg-white border border-gray-300 text-gray-900 text-sm rounded-md focus:ring-indigo-500 focus:border-indigo-500 leading-7 block w-full px-2.5 pe-8 py-1 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-indigo-500 dark:focus:border-indigo-500 outline-0"
                                        />
                                        {waitingRoomDate && (
                                            <button onClick={clearFilter}
                                                className="absolute inset-y-0 right-0 px-2 py-3 bg-gray-200 hover:bg-gray-300 text-gray-600 rounded-r-md"
                                            >
                                                <XMarkIcon className="h-4 w-4 text-gray-500" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className='flex items-center sm:ml-2 mt-2 sm:mt-0'>
                                    <p className="block text-sm font-medium leading-8 me-2 text-gray-800 dark:text-gray-200"> Status: </p>
                                    <Select
                                        name="status"
                                        value={waitingRoomStatus}
                                        options={ChartStatus}
                                        placeholder="Search.."
                                        isSearchable={false}
                                        menuPortalTarget={document.body}
                                        onChange={handelSelectStatus}
                                        className="w-full sm:min-w-full rounded-md react-select-container"
                                        classNamePrefix="react-select"
                                    />
                                </div>
                            </div>

                            <div className="flex flex-1 justify-center px-2 lg:ml-14 lg:justify-end mt-2 md:mt-0">
                                <div className="w-full max-w-lg lg:max-w-xs">
                                    <label className="sr-only">
                                        Search
                                    </label>
                                    <div className="relative">
                                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                        </div>
                                        <input
                                            type="search"
                                            name="search"
                                            ref={inputRef}
                                            className="bg-white border border-gray-300 text-gray-900 text-sm rounded-md focus:ring-indigo-500 focus:border-indigo-500 leading-7 block w-full pl-10 pe-2 py-1 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-indigo-500 dark:focus:border-indigo-500 outline-0"
                                            placeholder="Patient name or DOB"
                                            autoComplete='off'
                                            defaultValue={searchInputValue}
                                            onChange={handelSearchPatient}
                                            onKeyPress={handelSearchPatient}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='mt-2 xl:mt-0 lg:flex items-center justify-end'>
                            <div className='md:flex items-center justify-end'>
                                <div>
                                    {userInfo && userInfo.practice_info && userInfo.practice_info.length > 1 ? (
                                        <Select
                                            name="practice"
                                            value={selectedPractice}
                                            options={practiceOptions}
                                            placeholder="Practice"
                                            isSearchable={false}
                                            menuPortalTarget={document.body}
                                            onChange={handelSelectPractice}
                                            className='me-1 min-w-52 react-select-container'
                                            classNamePrefix="react-select"
                                        />
                                    )
                                    :
                                        <p className='me-1 p-1 px-2 border rounded-md text-gray-800 dark:text-gray-200'>{practiceOptions.map(option => option.label).join(', ')}</p>
                                    }
                                </div>

                                <div className='mt-2 sm:mt-0'>
                                    <button type="button" onClick={() => setAddPatientOpen(true)} className="bg-indigo-500 hover:bg-indigo-600 text-white py-1.5 ps-3 pe-4 rounded-md inline-flex items-center">
                                        <PlusSmallIcon className="h-5 w-5 text-white me-1" />
                                        <span>New Patient</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-4">
                        <h5 className='text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3'>All Charts :</h5>

                        <div className="" style={{ 'minHeight': '65vh' }}>

                            <div className={`h-96 text-center flex items-center justify-center ${loadDefaultData ? 'hidden' : 'block'}`}>                 
                                <div>
                                    <PulseLoader color="#4f46e5" />
                                    {/* <img className='invert-0 dark:invert opacity-20' src='/loader.gif' alt='Loader' ></img> */}
                                </div>
                            </div>

                            <div className={`grid grid-cols-1 gap-y-3 gap-x-4 sm:grid-cols-12 ${loadDefaultData ? 'block' : 'hidden'}`}>
                                {waitingRoomPatient && waitingRoomPatient.length > 0 ? (
                                    waitingRoomPatient.map((chart) => (
                                        <div key={chart.chart_id} className="col-span-12 sm:col-span-6 lg:col-span-4 xl:col-span-3">
                                            <div className="transition-all ease-in-out hover:-translate-1 hover:scale-105 duration-150 bg-gray-100 dark:bg-slate-800 px-1.5 py-12 text-center rounded-md cursor-pointer" onClick={() => handlePatientSelect(chart)}>
                                                <input className="hidden" id={`for_${chart.patient_id}`} type="radio" name="disease_id" defaultValue={chart.patient_id} required />
                                                <label className="cursor-pointer" htmlFor={`for_${chart.patient_id}`}>
                                                    <span className="text-base font-semibold mb-2 block text-gray-700 dark:text-gray-200">{chart.patient_name}</span>
                                                    <span className="text-sm block mb-1 text-gray-700 dark:text-gray-300">{chart.vt_name}</span>
                                                    <span className="text-sm font-normal block mb-1">
                                                        {chart.status === 1 ? 
                                                            <span className='text-red-500'>Incomplete</span>
                                                        : chart.status === 2 ? 
                                                            <span className='text-green-500'>Done</span> 
                                                        : '' }
                                                    </span>
                                                </label>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="col-span-12 text-center h-80 flex items-center justify-center opacity-70">
                                        <div className='text-center'>
                                            <svg data-slot="icon" fill="none" strokeWidth="1.5" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="52" height="52" className='mx-auto'>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.182 16.318A4.486 4.486 0 0 0 12.016 15a4.486 4.486 0 0 0-3.198 1.318M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Z"></path>
                                            </svg>
                                            <p className='mt-1'>No data found</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />


            {/* Patient Add modal */}
            <Transition.Root show={addPatientOpen} as={Fragment}>
                <Dialog as="div" className="relative z-40" onClose={setAddPatientOpen}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-600 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
                                    <div className="bg-gray-100 dark:bg-gray-700 px-4 py-3 flex items-center justify-between">
                                        <h4 className='text-xl font-bold dark:text-gray-100'>Add Patient</h4>
                                        <button type="button" className="rounded-md bg-white text-gray-400 hover:text-red-400 dark:bg-gray-800"
                                            onClick={() => setAddPatientOpen(false)}
                                        >
                                            <span className="sr-only">Close</span>
                                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                        </button>
                                    </div>

                                    <form autoComplete='off' onSubmit={handleAddPatient}>
                                        
                                        <div className=" px-4 pb-4 pt-4 sm:p-6 sm:pb-4">
                                            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                                                <input type='hidden' name='practice_id' defaultValue={practiceID} />
                                                <div className='col-span-2 sm:col-span-1'>
                                                    <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-200"> Last Name <span className='text-red-500'>*</span></label>
                                                    <input required type="text" name="last_name" placeholder="Last Name"
                                                        className="border border-gray-300 text-gray-900 text-sm rounded-md focus:ring-indigo-500 focus:border-indigo-500 leading-7 block w-full px-2.5 pe-8 py-1 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-indigo-500 dark:focus:border-indigo-500 outline-0 "
                                                    />
                                                </div>
                                                <div className='col-span-2 sm:col-span-1'>
                                                    <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-200"> First Name <span className='text-red-500'>*</span></label>
                                                    <input required type="text" name="first_name" placeholder="First Name"
                                                        className="border border-gray-300 text-gray-900 text-sm rounded-md focus:ring-indigo-500 focus:border-indigo-500 leading-7 block w-full px-2.5 pe-8 py-1 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-indigo-500 dark:focus:border-indigo-500 outline-0"
                                                    />
                                                </div>
                                                <div className='col-span-2 sm:col-span-1'>
                                                    <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-200"> DOB <span className='text-red-500'>*</span></label>
                                                    <input required type="text" name="dob" placeholder="MM/DD/YYYY"
                                                        className="flatpickr-date border border-gray-300 text-gray-900 text-sm rounded-md focus:ring-indigo-500 focus:border-indigo-500 leading-7 block w-full px-2.5 pe-8 py-1 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-indigo-500 dark:focus:border-indigo-500 outline-0"
                                                    />
                                                </div>
                                                <div className='col-span-2 sm:col-span-1'>
                                                    <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-200"> Gender <span className='text-red-500'>*</span></label>
                                                    <Select
                                                        required
                                                        name="sex"
                                                        options={GenderOptions}
                                                        placeholder="Search.."
                                                        isSearchable={true}
                                                        menuPortalTarget={document.body}
                                                        styles={{
                                                            menuPortal: (provided) => ({
                                                                ...provided,
                                                                zIndex: 9999,
                                                            }),
                                                            menu: (provided) => ({
                                                                ...provided,
                                                                fontSize: '14px',
                                                                color: '#000',
                                                                zIndex: 9999,
                                                            }),
                                                        }}
                                                        className="react-select-container"
                                                        classNamePrefix="react-select"
                                                    />
                                                </div>
                                                <div className='col-span-2 sm:col-span-1'>
                                                    <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-200"> Phone <span className='text-red-500'>*</span></label>
                                                    <input required type="text" name="phone" placeholder="Phone"
                                                        className="us_phone border border-gray-300 text-gray-900 text-sm rounded-md focus:ring-indigo-500 focus:border-indigo-500 leading-7 block w-full px-2.5 pe-8 py-1 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-indigo-500 dark:focus:border-indigo-500 outline-0"
                                                    />
                                                </div>
                                                <div className='col-span-2 sm:col-span-1'>
                                                    <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-200"> Visit Type <span className='text-red-500'>*</span></label>
                                                    <Select
                                                        required
                                                        name="vt_id"
                                                        options={vtOptions}
                                                        placeholder="Search.."
                                                        isSearchable={true}
                                                        menuPortalTarget={document.body}
                                                        styles={{
                                                            menuPortal: (provided) => ({
                                                                ...provided,
                                                                zIndex: 9999,
                                                            }),
                                                            menu: (provided) => ({
                                                                ...provided,
                                                                fontSize: '14px',
                                                                color: '#000',
                                                                zIndex: 9999,
                                                            }),
                                                        }}
                                                        className="react-select-container"
                                                        classNamePrefix="react-select"
                                                    />
                                                </div>
                                                <div className='col-span-2 sm:col-span-1'>
                                                    <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-200"> DOS <span className='text-red-500'>*</span></label>
                                                    <input required type="text" name="dos" placeholder="MM/DD/YYYY"
                                                        className="flatpickr-date border border-gray-300 text-gray-900 text-sm rounded-md focus:ring-indigo-500 focus:border-indigo-500 leading-7 block w-full px-2.5 pe-8 py-1 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-indigo-500 dark:focus:border-indigo-500 outline-0"
                                                    />
                                                </div>
                                                <div className='col-span-2 sm:col-span-1'>
                                                    <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-200"> Insurance Name </label>
                                                    <Select
                                                        name="insurance_id"
                                                        options={insurance}
                                                        placeholder="Search.."
                                                        isSearchable={true}
                                                        menuPortalTarget={document.body}
                                                        styles={{
                                                            menuPortal: (provided) => ({
                                                                ...provided,
                                                                zIndex: 9999,
                                                            }),
                                                            menu: (provided) => ({
                                                                ...provided,
                                                                fontSize: '14px',
                                                                color: '#000',
                                                                zIndex: 9999,
                                                            }),
                                                        }}
                                                        className="react-select-container"
                                                        classNamePrefix="react-select"
                                                    />
                                                </div>
                                                <div className='col-span-2 sm:col-span-2'>
                                                    <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-200"> Insurance ID </label>
                                                    <input type="text" name="insurance" placeholder="Insurance ID"
                                                        className="border border-gray-300 text-gray-900 text-sm rounded-md focus:ring-indigo-500 focus:border-indigo-500 leading-7 block w-full px-2.5 pe-8 py-1 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-indigo-500 dark:focus:border-indigo-500 outline-0"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-gray-100 dark:bg-gray-700 px-4 py-3 flex flex-row-reverse sm:px-6 ">
                                            <button type="submit"
                                                className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500  sm:w-auto ml-3"
                                            >
                                                Save
                                            </button>
                                            <button type="button" onClick={() => setAddPatientOpen(false)}
                                                className="inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-800 dark:ring-gray-800 dark:text-gray-100 px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:dark:bg-gray-900  hover:bg-gray-100 sm:mt-0 sm:w-auto "
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        
        </>
    )
}

export default WaitingRoom;
